import type { FlipAnimation } from '../flashcard/elements/registry'

export type DeckTemplateSettings = {
  width?: number
  height?: number
  darkMode?: boolean

  backgroundColor?: string
  themeBackgroundColor?: string

  borderColor?: string
  themeBorderColor?: string
  borderWidth?: number
  borderRadius?: number
  themeBorderRadius?: number

  shadow?: string

  /** If true, suppress autoplay behaviors (audio/video autoplay) within a flashcard context. */
  disableAutoplay?: boolean

  /** If true, force autoplay of audio elements (even if per-element autoplay is off). */
  audioAutoplay?: boolean

  /** If true, hide audio element UI (only meaningful with autoplay). */
  audioNotVisible?: boolean

  /** If true, always show audio UI buttons (overrides per-element/deck hidden audio). */
  audioForceVisible?: boolean

  /** If true, hide audio UI buttons when in flowchart mode (audio still plays via play-audio nodes). */
  flowchartHideAudio?: boolean

  transitionDuration?: number
  themeTransitionDuration?: number

  /** DeckPlayer card-to-card transition (NOT the flashcard flip). */
  cardChangeAnimation?: DeckPlayerCardChangeAnimation

  /** DeckPlayer card-to-card transition when the interaction is a success. */
  successCardChangeAnimation?: DeckPlayerCardChangeAnimation

  /** DeckPlayer card-to-card transition when the interaction is a failure. */
  failedCardChangeAnimation?: DeckPlayerCardChangeAnimation

  flipAnimation?: FlipAnimation
  themeFlipAnimation?: FlipAnimation
  hoverFlip?: boolean
  showBackFirst?: boolean

  /** Visual style for checkbox-group checked marks. */
  checkboxTickStyle?: CheckboxTickStyle

  /** Flowchart: if true, user must wait until midnight after completing a session before playing again. */
  deckPropagation?: boolean
  /** Flowchart: if true, show a "Continue" button on the end screen instead of requiring page reload. */
  showFlowNextButton?: boolean
}

export type CheckboxTickStyle = 'fill' | 'x' | 'haken'

export type DeckPlayerCardChangeAnimation =
  | 'none'
  | 'fade'
  | 'flip'
  | 'slide-left'
  | 'slide-right'
  | 'swipe-up'
  | 'swipe-down'
  | 'reveal-under'
  | 'scale'

const FLIP_ANIMATIONS: ReadonlySet<FlipAnimation> = new Set([
  'rotate-y',
  'rotate-x',
  'slide',
  'fade',
  'none',
])

const CARD_CHANGE_ANIMATIONS: ReadonlySet<DeckPlayerCardChangeAnimation> = new Set([
  'none',
  'fade',
  'flip',
  'slide-left',
  'slide-right',
  'swipe-up',
  'swipe-down',
  'reveal-under',
  'scale',
])

const toFlag = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (v === 'true' || v === '1' || v === 'yes' || v === 'on') return true
    if (v === 'false' || v === '0' || v === 'no' || v === 'off') return false
  }
  return undefined
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return undefined
}

const toNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length ? trimmed : undefined
}

const toColorStringOrUnset = (value: unknown): string | undefined => {
  // For colors, empty string is semantically "unset" in the Flashcard component.
  // Treat it as undefined so higher-level defaults don’t accidentally override.
  if (typeof value !== 'string') return undefined
  return value.trim().length ? value : undefined
}

const toFlipAnimation = (value: unknown): FlipAnimation | undefined => {
  if (typeof value !== 'string') return undefined
  if (FLIP_ANIMATIONS.has(value as FlipAnimation)) return value as FlipAnimation
  return undefined
}

const toCardChangeAnimation = (value: unknown): DeckPlayerCardChangeAnimation | undefined => {
  if (typeof value !== 'string') return undefined
  if (CARD_CHANGE_ANIMATIONS.has(value as DeckPlayerCardChangeAnimation)) {
    return value as DeckPlayerCardChangeAnimation
  }
  return undefined
}

const CHECKBOX_TICK_STYLES: ReadonlySet<CheckboxTickStyle> = new Set(['fill', 'x', 'haken'])

const toCheckboxTickStyle = (value: unknown): CheckboxTickStyle | undefined => {
  if (typeof value !== 'string') return undefined
  const v = value.trim().toLowerCase()
  // Back-compat / synonyms
  if (v === 'current' || v === 'default' || v === 'square') return 'fill'
  if (v === 'check' || v === 'checkmark' || v === 'tick') return 'haken'
  if (v === 'cross') return 'x'
  if (CHECKBOX_TICK_STYLES.has(v as CheckboxTickStyle)) return v as CheckboxTickStyle
  return undefined
}

export const coerceDeckTemplateSettings = (input: unknown): DeckTemplateSettings => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {}
  const raw = input as Record<string, unknown>

  return {
    width: toNumber(raw.width),
    height: toNumber(raw.height),
    darkMode: toFlag(raw.darkMode),

    backgroundColor: toColorStringOrUnset(raw.backgroundColor),
    themeBackgroundColor: toColorStringOrUnset(raw.themeBackgroundColor),

    borderColor: toColorStringOrUnset(raw.borderColor),
    themeBorderColor: toColorStringOrUnset(raw.themeBorderColor),
    borderWidth: toNumber(raw.borderWidth),
    borderRadius: toNumber(raw.borderRadius),
    themeBorderRadius: toNumber(raw.themeBorderRadius),

    // Shadow: allow 'none' (and other valid CSS strings). Empty string is treated as unset.
    shadow: toNonEmptyString(raw.shadow),

    // Autoplay suppression (useful for embeds and deck-level defaults)
    disableAutoplay:
      toFlag(raw.disableAutoplay) ??
      toFlag(raw.disable_autoplay) ??
      toFlag(raw.disableAudioAutoplay) ??
      toFlag(raw.disable_audio_autoplay),

    // Audio autoplay (separate from disableAutoplay because many WP embeds include
    // disableAutoplay:false by default, which should NOT force autoplay).
    audioAutoplay:
      toFlag(raw.audioAutoplay) ??
      toFlag(raw.audio_autoplay) ??
      toFlag(raw.autoplayAudio) ??
      toFlag(raw.autoplay_audio) ??
      toFlag(raw.forceAutoplayAudio) ??
      toFlag(raw.force_autoplay_audio),

    // Audio visibility
    audioNotVisible:
      toFlag(raw.audioNotVisible) ??
      toFlag(raw.audio_not_visible) ??
      toFlag(raw.audioHidden) ??
      toFlag(raw.audio_hidden) ??
      toFlag(raw.hideAudio) ??
      toFlag(raw.hide_audio),

    audioForceVisible:
      toFlag(raw.audioForceVisible) ??
      toFlag(raw.audio_force_visible) ??
      toFlag(raw.forceVisibleAudio) ??
      toFlag(raw.force_visible_audio) ??
      toFlag(raw.forceAudioVisible) ??
      toFlag(raw.force_audio_visible),

    flowchartHideAudio: toFlag(raw.flowchartHideAudio) ?? toFlag(raw.flowchart_hide_audio),

    transitionDuration: toNumber(raw.transitionDuration),
    themeTransitionDuration: toNumber(raw.themeTransitionDuration),

    cardChangeAnimation:
      toCardChangeAnimation(raw.cardChangeAnimation) ??
      toCardChangeAnimation(raw.card_change_animation) ??
      toCardChangeAnimation(raw.cardChangeTransition) ??
      toCardChangeAnimation(raw.card_change_transition),

    successCardChangeAnimation:
      toCardChangeAnimation(raw.successCardChangeAnimation) ??
      toCardChangeAnimation(raw.success_card_change_animation) ??
      toCardChangeAnimation(raw.successAnimation) ??
      toCardChangeAnimation(raw.success_animation) ??
      toCardChangeAnimation(raw.successCardChangeTransition) ??
      toCardChangeAnimation(raw.success_card_change_transition),

    failedCardChangeAnimation:
      toCardChangeAnimation(raw.failedCardChangeAnimation) ??
      toCardChangeAnimation(raw.failed_card_change_animation) ??
      toCardChangeAnimation(raw.failCardChangeAnimation) ??
      toCardChangeAnimation(raw.fail_card_change_animation) ??
      toCardChangeAnimation(raw.failedAnimation) ??
      toCardChangeAnimation(raw.failed_animation) ??
      toCardChangeAnimation(raw.failAnimation) ??
      toCardChangeAnimation(raw.fail_animation) ??
      toCardChangeAnimation(raw.failedCardChangeTransition) ??
      toCardChangeAnimation(raw.failed_card_change_transition) ??
      toCardChangeAnimation(raw.failCardChangeTransition) ??
      toCardChangeAnimation(raw.fail_card_change_transition),

    flipAnimation: toFlipAnimation(raw.flipAnimation),
    themeFlipAnimation: toFlipAnimation(raw.themeFlipAnimation),
    hoverFlip: toFlag(raw.hoverFlip) ?? toFlag(raw.hover_flip),
    showBackFirst:
      toFlag(raw.showBackFirst) ??
      toFlag(raw.show_back_first) ??
      toFlag(raw.backFirst) ??
      toFlag(raw.back_first),

    checkboxTickStyle:
      toCheckboxTickStyle(raw.checkboxTickStyle) ??
      toCheckboxTickStyle(raw.checkbox_tick_style) ??
      toCheckboxTickStyle(raw.checkboxStyle) ??
      toCheckboxTickStyle(raw.checkbox_style),

    deckPropagation: toFlag(raw.deckPropagation) ?? toFlag(raw.deck_propagation),
    showFlowNextButton: toFlag(raw.showFlowNextButton) ?? toFlag(raw.show_flow_next_button),
  }
}

export type ResolveDeckTemplateSettingsInput = {
  /** Highest-priority user settings; currently supports only forceDarkMode. */
  user?: {
    forceDarkMode?: boolean
    settings?: DeckTemplateSettings | null
  }

  /** Deck template settings (saved in deck editor, stored in _pfc_deck_props meta). */
  deck?: DeckTemplateSettings | null

  /** Per-card props from card JSON (lowest priority). */
  card?: DeckTemplateSettings | null

  /**
   * Per-embed overrides (shortcode / widget attributes set by the site admin).
   * Applied after user settings but before deck settings — use sparingly, intended
   * only for forced site-level overrides (e.g. force dark mode on a specific page).
   * @deprecated Prefer deck settings for configuration. Embed props will be
   *   restricted to site-admin-only overrides in a future version.
   */
  embed?: DeckTemplateSettings | null
}

const pick = <K extends keyof DeckTemplateSettings>(
  key: K,
  sources: Array<DeckTemplateSettings | null | undefined>,
): DeckTemplateSettings[K] | undefined => {
  for (const src of sources) {
    if (!src) continue
    const value = src[key]
    if (value !== undefined) return value
  }
  return undefined
}

/**
 * Resolves the effective template settings with priority:
 *   user settings > deck settings > card settings
 *
 * Embed (shortcode) props are intentionally placed AFTER deck settings so that
 * deck-level configuration is authoritative. Embed props only act as a final
 * fallback below card settings, except for forced overrides handled separately.
 */
export const resolveDeckTemplateSettings = (
  input: ResolveDeckTemplateSettingsInput,
): DeckTemplateSettings => {
  // Priority order: user settings → deck → card → embed (lowest fallback)
  const sources = [
    input.user?.settings ?? null,
    input.deck ?? null,
    input.card ?? null,
    input.embed ?? null,
  ]

  const legacyCardChangeAnimation = pick('cardChangeAnimation', sources)

  const resolved: DeckTemplateSettings = {
    width: pick('width', sources),
    height: pick('height', sources),
    darkMode: pick('darkMode', sources),

    backgroundColor: pick('backgroundColor', sources),
    themeBackgroundColor: pick('themeBackgroundColor', sources),

    borderColor: pick('borderColor', sources),
    themeBorderColor: pick('themeBorderColor', sources),
    borderWidth: pick('borderWidth', sources),
    borderRadius: pick('borderRadius', sources),
    themeBorderRadius: pick('themeBorderRadius', sources),

    shadow: pick('shadow', sources),

    disableAutoplay: pick('disableAutoplay', sources),

    audioAutoplay: pick('audioAutoplay', sources),

    audioNotVisible: pick('audioNotVisible', sources),
    audioForceVisible: pick('audioForceVisible', sources),

    transitionDuration: pick('transitionDuration', sources),
    themeTransitionDuration: pick('themeTransitionDuration', sources),

    cardChangeAnimation: legacyCardChangeAnimation,
    successCardChangeAnimation:
      pick('successCardChangeAnimation', sources) ?? legacyCardChangeAnimation,
    failedCardChangeAnimation:
      pick('failedCardChangeAnimation', sources) ?? legacyCardChangeAnimation,

    flipAnimation: pick('flipAnimation', sources),
    themeFlipAnimation: pick('themeFlipAnimation', sources),
    hoverFlip: pick('hoverFlip', sources),
    showBackFirst: pick('showBackFirst', sources),

    checkboxTickStyle: pick('checkboxTickStyle', sources),

    flowchartHideAudio: pick('flowchartHideAudio', sources),
  }

  // User override: force dark mode ON (never forces it off).
  if (input.user?.forceDarkMode === true) {
    resolved.darkMode = true
  }

  return resolved
}
